/**
 * rasterReadiness.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * «أي ساعات الشريط الزمني جاهزة فعلاً؟»
 *
 * الإحماء نافذة متحرّكة حول الموضع الحالي (‏±8 خارج التشغيل، +16 أثناءه)، لا الشريط
 * كلّه. فالانتقال إلى ساعة خارج النافذة يعني انتظار تنزيل وفكّ — وهو التوقّف الذي يراه
 * المستخدم بلا تفسير: الشريط يبدو موحّداً، فيظنّ أن البطء عشوائي.
 *
 * هذه الوحدة تنشر حالة الجاهزية كي يرسمها الشريط، وتقود بها **وضع تركيز الطابور**
 * (راجع `applyFocus` أدناه). لا تُشغّل تنزيلاً ولا تُلغي مهمّة ولا تمسّ الجودة — تُرتّب
 * فقط: من يأخذ النطاق أولاً حين ينتظر المستخدم لحظةً بعينها.
 *
 * لماذا استطلاع دوري لا إشعار من الكاش: الجاهزية تتغيّر من مصادر عدّة (الطابور العاجل،
 * التعبئة الخلفية، الطرد بـ LRU، تبديل النموذج). ربط كلٍّ منها بإشعار يعني تشعّباً في
 * أربعة ملفّات مقابل مسح مصفوفة صغيرة مرّتين في الثانية — 121 إطاراً × نوعين = 242 بحثاً
 * في Map، أي أجزاء من المللي ثانية. والاستطلاع لا يعمل أصلاً إلا وللشريط مشترك.
 */

import { isRasterBytesReady } from './rasterImageCache';
import { layerFrameUrl } from './rasterVersion';
import { setPrefetchFocus } from './prefetchQueue';

export const FRAME_MISSING = 0;
export const FRAME_READY = 1;

interface Source {
    dir: string;
    /** كل الأنواع التي يحتاجها الإطار ليُعرض كاملاً (الطبقة + الرياح + مدخلات الأمطار). */
    types: string[];
    frames: number;
    /**
     * روابط صريحة لكل إطار — بديلٌ عن `dir/types/index`.
     *
     * محورُ الرادار لا تصفه الثلاثية: إطاراته ملفّات WebP أسماؤها **أوقات رصد** لا
     * أرقام ساعات، وقد تتوزّع على عدّة نطاقات (بلاطات) لكل إطار. فبدل حشر ذلك في
     * قالبٍ لم يُصمَّم له، نمرّر الروابط كما هي. والمقياس يبقى واحداً — `isRasterBytesReady`
     * تسأل عن رابط، لا تعرف ولا تحتاج أن تعرف من أين جاء.
     */
    frameUrls?: string[][];
}

const EMPTY = new Uint8Array(0);

let source: Source | null = null;
let snapshot: Uint8Array = EMPTY;
let listeners = new Set<() => void>();
let timer: number | null = null;

/** التردّد: نصف ثانية. أسرع من ذلك لا يضيف معلومة (التنزيل نفسه أبطأ)، وأبطأ يبدو متأخّراً. */
const POLL_MS = 500;

function compute(): Uint8Array {
    if (!source || source.frames <= 0) return EMPTY;
    if (source.frameUrls) {
        const next = new Uint8Array(source.frames);
        for (let idx = 0; idx < source.frames; idx++) {
            const urls = source.frameUrls[idx];
            // إطارٌ بلا روابط ليس «جاهزاً» بل **غير معروف** — ونعدّه غير جاهز، فالوعد
            // بالجاهزية أسوأ من إنكارها: الأوّل يُنتج تشغيلاً متلعثماً بلا تفسير.
            next[idx] = urls && urls.length > 0 && urls.every(isRasterBytesReady)
                ? FRAME_READY : FRAME_MISSING;
        }
        return next;
    }
    if (source.types.length === 0) return EMPTY;
    const { dir, types, frames } = source;
    const next = new Uint8Array(frames);
    for (let idx = 0; idx < frames; idx++) {
        // «جاهز» = **بايتات كل** مدخلات الإطار بحوزتنا. جزءٌ منها يعني أن الانتقال
        // سينتظر الباقي على الشبكة، فالإطار الجزئي يُعدّ غير جاهز.
        //
        // المقياس البايتات لا الفكّ: كاش الفكّ سعته أربعون ويطرد بـLRU، فلو قِسنا به
        // لما تجاوز الشريط عشرين ساعة زرقاء أبداً — ولنسي ما مرّ عليه كلّما دار الطرد.
        // البايتات تراكمية لا تُطرد، فيمتلئ الخط ويبقى ممتلئاً، وهو الصحيح: ما وصل
        // فكّه أجزاء من الثانية، وما لم يصل رحلةُ شبكة كاملة.
        let ready = true;
        for (const type of types) {
            // عبر السجلّ: الطبقة المستقلّة قد تعيش في مجلّد وامتداد وإصدار غير هذه —
            // وقياس رابط لم يُطلب قطّ يعني «غير جاهز» أبداً، أي شريط فارغ وتشغيل لا يتقدّم.
            if (!isRasterBytesReady(layerFrameUrl(dir, type, idx))) { ready = false; break; }
        }
        next[idx] = ready ? FRAME_READY : FRAME_MISSING;
    }
    return next;
}

/**
 * ── قيادة وضع تركيز الطابور ───────────────────────────────────────────────────
 *
 * الجاهزية تُستطلَع هنا أصلاً، فهذا المكان الطبيعي الوحيد الذي يعرف — مرّتين في الثانية
 * وبلا كلفة إضافية — أن **الإطار المعروض الآن ليس جاهزاً**. عندها نضيّق الطابور كي يذهب
 * النطاق كلّه إلى ما ينتظره المستخدم، ثم نُرخيه فور وصوله.
 *
 * بديلُه ربطُ ذلك بإعادة رسم React على كل تغيّر جاهزية — كلفة رسم كامل شجرة الخريطة
 * مرّتين في الثانية مقابل لا شيء. لذلك يقودها المستطلِع مباشرةً.
 */
let focusIndex: number | null = null;

/** يُعلن الموضع المعروض حالياً (فهرس ساعة النموذج، لا موضع المحور). `null` يُلغي التركيز. */
export function setReadinessFocus(index: number | null): void {
    if (focusIndex === index) return;
    focusIndex = index;
    // موضع جديد = انتظار جديد: يُستأنف السقف من الصفر بدل أن يرث تعثّر الموضع السابق.
    focusSince = 0;
    applyFocus();
}

/**
 * سقف زمني للتركيز. إطارٌ مفقود على الخادم لا يصبح جاهزاً أبداً، فبلا هذا السقف يبقى
 * الصفّ الخلفي موقوفاً إلى الأبد — أي يتحوّل تحسينُ الانتظار إلى تعطيل دائم للتعبئة.
 */
const FOCUS_MAX_MS = 8000;
let focusSince = 0;

function applyFocus() {
    const want = focusIndex !== null
        && focusIndex >= 0
        && focusIndex < snapshot.length
        && snapshot[focusIndex] !== FRAME_READY;
    if (!want) {
        focusSince = 0;
        setPrefetchFocus(false);
        return;
    }
    const now = Date.now();
    if (focusSince === 0) focusSince = now;
    setPrefetchFocus(now - focusSince < FOCUS_MAX_MS);
}

function tick() {
    const next = compute();
    let changed = next.length !== snapshot.length;
    if (!changed) {
        for (let i = 0; i < next.length; i++) {
            if (next[i] !== snapshot[i]) { changed = true; break; }
        }
    }
    // مرجع ثابت عند عدم التغيّر — شرط `useSyncExternalStore` لتفادي إعادة رسم لا داعي لها.
    if (changed) {
        snapshot = next;
        for (const fn of listeners) fn();
    }
    // يُقيَّم دائماً، لا عند التغيّر فقط: قد يتحرّك المؤشّر دون أن تتغيّر خريطة الجاهزية.
    applyFocus();
}

function startTimer() {
    if (timer !== null || listeners.size === 0) return;
    timer = window.setInterval(tick, POLL_MS);
}

function stopTimer() {
    if (timer === null) return;
    window.clearInterval(timer);
    timer = null;
}

/**
 * تُعلن الخريطة عن مدخلات الإطار الحالية (تتغيّر بتبديل الطبقة/النموذج/الجسيمات).
 * تمرير `null` يُعطّل المؤشّر — يُستعمل على محور القمر الصناعي حيث فهرس الشريط ليس
 * ساعة نموذج، فرسم جاهزية النُسج عليه يكذب على المستخدم.
 */
export function setReadinessSource(next: Source | null): void {
    const sameUrls = (a?: string[][], b?: string[][]) => {
        if (!a && !b) return true;
        if (!a || !b || a.length !== b.length) return false;
        return a.every((row, i) => row.length === b[i].length && row.every((u, j) => u === b[i][j]));
    };
    const same = source && next
        && source.dir === next.dir
        && source.frames === next.frames
        && source.types.length === next.types.length
        && source.types.every((t, i) => t === next.types[i])
        && sameUrls(source.frameUrls, next.frameUrls);
    if (same) return;
    source = next;
    // تحديث فوري: تبديل الطبقة يجب أن يُظهر خريطة جاهزية الطبقة الجديدة الآن لا بعد نصف ثانية.
    tick();
}

export function subscribeReadiness(fn: () => void): () => void {
    listeners.add(fn);
    startTimer();
    return () => {
        listeners.delete(fn);
        if (listeners.size === 0) stopTimer();
    };
}

export function getReadinessSnapshot(): Uint8Array {
    return snapshot;
}
